import { useState, useEffect, useCallback } from 'react';
import './RadarTab.css';
import { supabase } from './supabase';
import { useEffectiveUser } from './ImpersonationContext';
import Badge from './components/Badge';
import StatCard from './components/StatCard';

const VPS = 'https://copyccxx.com';

function timeAgo(isoTs) {
  if (!isoTs) return '—';
  const diff = Math.floor((Date.now() - new Date(isoTs).getTime()) / 1000);
  if (diff < 60)    return 'il y a quelques secondes';
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)} jours`;
}

const truncAddr = a => `${a.slice(0, 6)}…${a.slice(-4)}`;
const fmtUsd = v => v == null ? '—' : `$${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}`;
const fmtPct = v => v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(0)}%`;

const DEFAULT_SETTINGS = {
  enabled:                    false,
  min_amount_usd:             200,
  tier1_max_min:              15,
  tier2_max_min:              60,
  min_gain_pct:               50,
  min_token_gain_24h_pct:     30,
  max_tokens_per_day:         60,
  max_wallets:                500,
  min_wallet_age_days:        7,
  max_candidates_per_token:   3,
  max_pages_wallet_scoring:   3,
  max_pages_pump_buyers:      2,
  cache_duration_hours:       24,
};

// SVG cohérents avec AnalyticsTab (stroke #00e5cc, 16×16)
const IconRadar = (
  <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
    <circle cx="10" cy="10" r="2" fill="#00e5cc"/>
    <circle cx="10" cy="10" r="5" stroke="#00e5cc" strokeWidth="1.3" opacity=".6"/>
    <circle cx="10" cy="10" r="8" stroke="#00e5cc" strokeWidth="1.3" opacity=".3"/>
  </svg>
);
const IconSniper = (
  <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
    <circle cx="10" cy="10" r="6" stroke="#00e5cc" strokeWidth="1.3"/>
    <circle cx="10" cy="10" r="2" fill="#00e5cc"/>
    <path d="M10 2v3M10 15v3M2 10h3M15 10h3" stroke="#00e5cc" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);
const IconEarly = (
  <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
    <path d="M11 2L4 11h7l-2 7 9-10h-7l2-6z" stroke="#00e5cc" strokeWidth="1.3" strokeLinejoin="round"/>
  </svg>
);

function getMonthKeyRadar() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
}

const BUDGET_PROFILES = [
  { minDaily:1250000, label:'MAXIMUM',  icon:'⚡', maxCandidates:5, pagesScoring:5, pagesPump:5, intervalH: 1 },
  { minDaily:200000,  label:'PROFOND',  icon:'🔥', maxCandidates:3, pagesScoring:3, pagesPump:2, intervalH: 2 },
  { minDaily: 50000, label:'NORMAL',    icon:'⚖️',  maxCandidates:3, pagesScoring:2, pagesPump:1, intervalH: 4 },
  { minDaily: 10000, label:'ECO',       icon:'🌿', maxCandidates:2, pagesScoring:1, pagesPump:1, intervalH: 8 },
  { minDaily:     0, label:'ULTRA-ECO', icon:'🪶', maxCandidates:1, pagesScoring:1, pagesPump:1, intervalH:12 },
];

function getBudgetProfileUI(budgetDaily) {
  return BUDGET_PROFILES.find(p => budgetDaily >= p.minDaily) || BUDGET_PROFILES[BUDGET_PROFILES.length - 1];
}

export default function RadarTab({ onCopyWallet }) {
  const { effectiveUserId, isImpersonating } = useEffectiveUser();

  const [wallets,        setWallets]        = useState([]);
  const [settings,       setSettings]       = useState(null);
  const [lastScan,       setLastScan]       = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [settingsOpen,   setSettingsOpen]   = useState(false);
  const [settingsForm,   setSettingsForm]   = useState(DEFAULT_SETTINGS);
  const [savingSettings, setSavingSettings] = useState(false);
  const [rescoringMsg,   setRescoringMsg]   = useState(null);
  const [confirmRescore, setConfirmRescore] = useState(null);
  const [filterStatus,   setFilterStatus]   = useState('all');
  const [copiedId,       setCopiedId]       = useState(null);
  const [sortKey,        setSortKey]        = useState('follow_score');
  const [sortDir,        setSortDir]        = useState('desc');
  const [budgetInfo,     setBudgetInfo]     = useState(null);

  const loadAll = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = effectiveUserId || session?.user?.id;
    if (!userId) { setLoading(false); return; }

    const [{ data: ws }, { data: st }, { data: log }, rotRes, budgetRes] = await Promise.all([
      supabase.from('radar_wallets')
        .select('*')
        .eq('user_id', userId)
        .order('follow_score',    { ascending: false, nullsFirst: false })
        .order('discovery_score', { ascending: false }),
      supabase.from('radar_settings')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle(),
      supabase.from('radar_scan_log')
        .select('scanned_at, tokens_scanned, wallets_added, wallets_evicted')
        .eq('user_id', userId)
        .eq('status', 'success')
        .order('scanned_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase.from('helius_keys').select('id', { count:'exact', head:true }).eq('in_rotation', true).is('blocked_at', null),
      supabase.from('radar_budget').select('credits_used').eq('month', getMonthKeyRadar()).maybeSingle(),
    ]);

    setWallets(ws || []);
    setSettings(st || null);
    setSettingsForm(st ? { ...DEFAULT_SETTINGS, ...st } : DEFAULT_SETTINGS);
    setLastScan(log || null);

    const rotCount         = rotRes?.count || 0;
    const budgetTotal      = rotCount * 1_000_000;
    const creditsUsed      = budgetRes?.data?.credits_used || 0;
    const creditsRemaining = Math.max(0, budgetTotal - creditsUsed);
    const now              = new Date();
    const daysLeft         = Math.max(1, new Date(now.getFullYear(), now.getMonth()+1, 0).getDate() - now.getDate() + 1);
    const budgetDaily      = Math.round(creditsRemaining / daysLeft);
    const profile          = getBudgetProfileUI(budgetDaily);
    setBudgetInfo({ rotCount, budgetTotal, creditsUsed, creditsRemaining, daysLeft, budgetDaily, profile });

    setLoading(false);
  }, [effectiveUserId]);

  useEffect(() => {
    setLoading(true);
    loadAll();

    let sub = null, budgetSub = null;
    supabase.auth.getSession().then(({ data: { session } }) => {
      const userId = effectiveUserId || session?.user?.id;
      if (!userId) return;
      sub = supabase.channel(`radar-wallets-${userId}`)
        .on('postgres_changes',
          { event: '*', schema: 'public', table: 'radar_wallets', filter: `user_id=eq.${userId}` },
          loadAll)
        .subscribe();
      budgetSub = supabase.channel('radar-budget-rt')
        .on('postgres_changes', { event: '*',      schema: 'public', table: 'radar_budget'  }, loadAll)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'helius_keys'   }, loadAll)
        .subscribe();
    });
    return () => { sub?.unsubscribe(); budgetSub?.unsubscribe(); };
  }, [effectiveUserId, loadAll]);

  const handleToggle = async () => {
    if (isImpersonating) return;
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return;
    const newEnabled = !(settings?.enabled ?? false);
    await supabase.from('radar_settings').upsert(
      { user_id: userId, ...DEFAULT_SETTINGS, ...(settings || {}), enabled: newEnabled },
      { onConflict: 'user_id' }
    );
    setSettings(s => ({ ...DEFAULT_SETTINGS, ...(s || {}), enabled: newEnabled }));
  };

  const handleDeleteWallet = async (wallet) => {
    if (isImpersonating) return;
    if (!window.confirm(`Supprimer ${truncAddr(wallet.wallet_address)} du Radar ?`)) return;
    setWallets(prev => prev.filter(w => w.id !== wallet.id));
    await supabase.from('radar_wallets').delete().eq('id', wallet.id);
  };

  const RESCORE_WARN_THRESHOLD = 50;

  const doRescore = async () => {
    setConfirmRescore(null);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    setRescoringMsg(null);
    try {
      const r = await fetch(`${VPS}/api/radar/rescore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      });
      if (r.ok) {
        setRescoringMsg('Rescoring lancé — résultats disponibles sous quelques minutes.');
        setTimeout(() => setRescoringMsg(null), 8000);
      } else {
        const e = await r.json().catch(() => ({}));
        setRescoringMsg(`Erreur : ${e.error || r.status}`);
      }
    } catch (e) {
      setRescoringMsg(`Erreur réseau : ${e.message}`);
    }
  };

  const handleRescore = () => {
    if (isImpersonating) return;
    const cacheHours = settings?.cache_duration_hours ?? 24;
    const toRescore = wallets.filter(w => {
      if (!w.last_score_at) return true;
      return (Date.now() - new Date(w.last_score_at).getTime()) / 3600000 >= cacheHours;
    });
    if (toRescore.length > RESCORE_WARN_THRESHOLD) {
      setConfirmRescore({ count: toRescore.length, creditEst: Math.round(toRescore.length * 990) });
      return;
    }
    doRescore();
  };

  const handleSaveSettings = async () => {
    if (isImpersonating) return;
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return;
    setSavingSettings(true);
    const payload = {
      user_id:                userId,
      enabled:                settings?.enabled ?? false,
      min_amount_usd:         Number(settingsForm.min_amount_usd),
      tier1_max_min:          Number(settingsForm.tier1_max_min),
      tier2_max_min:          Number(settingsForm.tier2_max_min),
      min_gain_pct:           Number(settingsForm.min_gain_pct),
      min_token_gain_24h_pct: Number(settingsForm.min_token_gain_24h_pct),
      max_tokens_per_day:     Number(settingsForm.max_tokens_per_day),
      max_wallets:               Number(settingsForm.max_wallets),
      min_wallet_age_days:       Number(settingsForm.min_wallet_age_days),
    };
    const { data } = await supabase.from('radar_settings')
      .upsert(payload, { onConflict: 'user_id' })
      .select()
      .maybeSingle();
    if (data) setSettings(data);
    setSavingSettings(false);
  };

  const handleCopy = (w) => {
    navigator.clipboard.writeText(w.wallet_address);
    setCopiedId(w.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const enabled        = settings?.enabled ?? false;
  const sniperCount    = wallets.filter(w => w.tier === 'sniper').length;
  const earlyCount     = wallets.filter(w => w.tier === 'early_mover').length;
  const watchingCount  = wallets.filter(w => (w.discovery_status ?? 'validated') === 'watching').length;
  const visibleWallets = filterStatus === 'all'
    ? wallets
    : wallets.filter(w => (w.discovery_status ?? 'validated') === filterStatus);

  const sortedWallets = [...visibleWallets].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  if (loading) {
    return <div className="rd-root"><div className="rd-loading">Chargement du Radar…</div></div>;
  }

  return (
    <div className="rd-root">

      {/* ── Header ── */}
      <div className="rd-header">
        <div className="rd-header-left">
          <button
            className={`rd-toggle ${enabled ? 'rd-toggle--on' : 'rd-toggle--off'}`}
            onClick={handleToggle}
            disabled={isImpersonating}
            title={enabled ? 'Désactiver le Radar' : 'Activer le Radar'}
          >
            <span className="rd-toggle-thumb" />
          </button>
          <span className="rd-toggle-label">
            Radar <span className={enabled ? 'rd-on' : 'rd-off'}>{enabled ? 'ACTIF' : 'DÉSACTIVÉ'}</span>
          </span>
          {lastScan ? (
            <span className="rd-lastscan">
              · Dernier scan {timeAgo(lastScan.scanned_at)}
              <span className="rd-lastscan-detail">
                &nbsp;({lastScan.tokens_scanned} tokens · +{lastScan.wallets_added} wallets)
              </span>
            </span>
          ) : (
            <span className="rd-lastscan">· Aucun scan effectué</span>
          )}
        </div>
        <div className="rd-header-right">
          <button className="rd-btn rd-btn--ghost" onClick={() => setSettingsOpen(o => !o)}>
            ⚙ Réglages
          </button>
          <button
            className="rd-btn rd-btn--accent"
            onClick={handleRescore}
            disabled={isImpersonating || wallets.length === 0}
            title={wallets.length === 0 ? 'Aucun wallet à scorer' : ''}
          >
            ⟳ Relancer le scoring
          </button>
        </div>
      </div>

      {/* ── Feedback rescore ── */}
      {rescoringMsg && (
        <div className="rd-banner rd-banner--teal">{rescoringMsg}</div>
      )}
      {confirmRescore && (
        <div className="rd-banner rd-banner--warning" style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
          <span>⚠ <b>{confirmRescore.count} wallets</b> à rescorer (~<b>{confirmRescore.creditEst.toLocaleString()}</b> crédits Helius estimés). Confirmer ?</span>
          <div style={{display:'flex',gap:8,flexShrink:0}}>
            <button onClick={doRescore} style={{padding:'4px 14px',borderRadius:5,background:'#f59e0b',border:'none',color:'#000',fontWeight:700,cursor:'pointer',fontSize:11}}>Confirmer</button>
            <button onClick={()=>setConfirmRescore(null)} style={{padding:'4px 14px',borderRadius:5,background:'transparent',border:'1px solid #5a6a80',color:'#5a6a80',fontWeight:700,cursor:'pointer',fontSize:11}}>Annuler</button>
          </div>
        </div>
      )}

      {/* ── Banner désactivé ── */}
      {!enabled && (
        <div className="rd-banner rd-banner--warning">
          Radar désactivé — aucun scan automatique ne sera effectué. Active le toggle pour démarrer la découverte quotidienne.
        </div>
      )}

      {/* ── Bannière budget adaptatif ── */}
      {budgetInfo && (
        <div style={{padding:'12px 14px',background:'rgba(124,77,255,0.06)',border:'1px solid #7c4dff22',borderRadius:8,marginBottom:10}}>
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:6}}>
            <span style={{fontWeight:800,color:'#c0c8d8',fontSize:12}}>{budgetInfo.profile.icon} {budgetInfo.profile.label}</span>
            <span style={{color:'#3a4a5a'}}>·</span>
            <span style={{fontSize:11,color:'#7c4dff'}}>{budgetInfo.rotCount} clé{budgetInfo.rotCount !== 1 ? 's' : ''} en rotation</span>
            <span style={{color:'#3a4a5a'}}>=</span>
            <span style={{fontSize:11,color:'#c0c8d8'}}>{(budgetInfo.budgetTotal/1e6).toFixed(0)}M cr ce mois</span>
          </div>
          <div style={{display:'flex',gap:16,flexWrap:'wrap',fontSize:10,color:'#5a6a80',marginBottom:5}}>
            <span>✅ Consommé : <b style={{color:'#c0c8d8'}}>{budgetInfo.creditsUsed.toLocaleString()}</b></span>
            <span>📦 Restant : <b style={{color:'#00e5cc'}}>{budgetInfo.creditsRemaining.toLocaleString()}</b> sur {budgetInfo.daysLeft}j</span>
            <span>📊 Budget/j : <b style={{color:'#c0c8d8'}}>{budgetInfo.budgetDaily.toLocaleString()}</b></span>
          </div>
          <div style={{fontSize:10,color:'#3a4a5a'}}>
            Scan toutes les <b style={{color:'#c0c8d8'}}>{budgetInfo.profile.intervalH}h</b> · <b style={{color:'#c0c8d8'}}>{budgetInfo.profile.maxCandidates}</b> candidats/token · <b style={{color:'#c0c8d8'}}>{budgetInfo.profile.pagesScoring}</b> pages scoring · <b style={{color:'#c0c8d8'}}>{budgetInfo.profile.pagesPump}</b> page pump
          </div>
        </div>
      )}

      {/* ── Panneau réglages repliable ── */}
      {settingsOpen && (
        <div className="rd-settings-panel">
          <div className="rd-settings-title">Paramètres du scan</div>
          <div className="rd-settings-grid">
            {[
              { key: 'min_amount_usd',         label: 'Mise min.',                       unit: '$' },
              { key: 'tier1_max_min',          label: 'Sniper — fenêtre max',            unit: 'min' },
              { key: 'tier2_max_min',          label: 'Early mover — fenêtre max',       unit: 'min' },
              { key: 'min_gain_pct',           label: 'Gain wallet min.',                unit: '%' },
              { key: 'min_token_gain_24h_pct', label: 'Gain token h24 min.',             unit: '%' },
              { key: 'max_tokens_per_day',     label: 'Tokens analysés / scan (max)',    unit: '' },
              { key: 'max_wallets',            label: 'Wallets max en base',             unit: '' },
              { key: 'min_wallet_age_days',    label: 'Protection nouvelles découvertes', unit: 'j' },
            ].map(({ key, label, unit }) => (
              <label key={key} className="rd-setting-field">
                <span className="rd-setting-label">{label}</span>
                <div className="rd-setting-input-wrap">
                  <input
                    type="number"
                    className="rd-setting-input"
                    value={settingsForm[key] ?? ''}
                    onChange={e => setSettingsForm(f => ({ ...f, [key]: e.target.value }))}
                    disabled={isImpersonating}
                  />
                  {unit && <span className="rd-setting-unit">{unit}</span>}
                </div>
              </label>
            ))}
          </div>
          <button
            className="rd-btn rd-btn--save"
            onClick={handleSaveSettings}
            disabled={savingSettings || isImpersonating}
          >
            {savingSettings ? 'Sauvegarde…' : 'Sauvegarder les réglages'}
          </button>
        </div>
      )}

      {/* ── StatCards ── */}
      <div className="rd-stats-row">
        <StatCard
          label="Wallets découverts"
          value={wallets.length}
          sub={`sur ${settings?.max_wallets ?? 500} max`}
          trend="neutral"
          icon={IconRadar}
        />
        <StatCard
          label="Snipers"
          value={sniperCount}
          sub="entrée ≤ tier1"
          trend={sniperCount > 0 ? 'up' : 'neutral'}
          icon={IconSniper}
        />
        <StatCard
          label="Early Movers"
          value={earlyCount}
          sub="entrée tier1 → tier2"
          trend={earlyCount > 0 ? 'accent' : 'neutral'}
          icon={IconEarly}
        />
      </div>

      {/* ── Filter bar ── */}
      {wallets.length > 0 && (
        <div className="rd-filter-bar">
          {[
            { key: 'all',       label: 'Tous',         count: wallets.length },
            { key: 'validated', label: 'Validés',      count: wallets.length - watchingCount },
            { key: 'watching',  label: 'À surveiller', count: watchingCount },
          ].map(({ key, label, count }) => (
            <button
              key={key}
              className={`rd-filter-btn${filterStatus === key ? ' rd-filter-btn--active' : ''}`}
              onClick={() => setFilterStatus(key)}
            >
              {label}
              <span className="rd-filter-count">{count}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Table wallets ── */}
      {wallets.length === 0 ? (
        <div className="rd-empty">
          {enabled
            ? 'Aucun wallet découvert pour l\'instant — le scanner tournera cette nuit.'
            : 'Active le Radar pour commencer la découverte automatique de wallets.'}
        </div>
      ) : visibleWallets.length === 0 ? (
        <div className="rd-empty rd-empty--filtered">Aucun wallet dans cette catégorie.</div>
      ) : (
        <div className={`rd-table-wrap${!enabled ? ' rd-table-wrap--dimmed' : ''}`}>
          <table className="rd-table">
            <thead>
              <tr>
                <th>Wallet</th>
                <th>Tier</th>
                <th>Token découverte</th>
                <th className={`rd-th-sort${sortKey==='gain_pct'?' rd-th-sort--active':''}`} onClick={()=>handleSort('gain_pct')}>
                  Gain token<span className="rd-sort-icon">{sortKey==='gain_pct'?(sortDir==='desc'?'↓':'↑'):'↕'}</span>
                </th>
                <th className={`rd-th-sort${sortKey==='cumul_amount_usd'?' rd-th-sort--active':''}`} onClick={()=>handleSort('cumul_amount_usd')}>
                  Mise<span className="rd-sort-icon">{sortKey==='cumul_amount_usd'?(sortDir==='desc'?'↓':'↑'):'↕'}</span>
                </th>
                <th className={`rd-th-sort${sortKey==='discovery_score'?' rd-th-sort--active':''}`} onClick={()=>handleSort('discovery_score')}>
                  Score radar<span className="rd-sort-icon">{sortKey==='discovery_score'?(sortDir==='desc'?'↓':'↑'):'↕'}</span>
                </th>
                <th className={`rd-th-sort${sortKey==='follow_score'?' rd-th-sort--active':''}`} onClick={()=>handleSort('follow_score')}>
                  Score fiabilité<span className="rd-sort-icon">{sortKey==='follow_score'?(sortDir==='desc'?'↓':'↑'):'↕'}</span>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedWallets.map(w => (
                <tr key={w.id} className="rd-row">
                  <td className="rd-cell-addr">
                    <button
                      className="rd-addr-copy"
                      onClick={() => handleCopy(w)}
                      title={copiedId === w.id ? 'Copié !' : "Copier l'adresse"}
                    >
                      {copiedId === w.id ? '✓ Copié' : truncAddr(w.wallet_address)}
                    </button>
                    <a
                      href={`https://solscan.io/account/${w.wallet_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rd-addr-ext"
                      title="Ouvrir dans Solscan"
                    >↗</a>
                  </td>
                  <td>
                    <div className="rd-tier-cell">
                      {w.tier === 'sniper'
                        ? <Badge variant="live">SNIPER</Badge>
                        : <Badge variant="warning">EARLY</Badge>}
                      {(w.discovery_status ?? 'validated') === 'watching' && (
                        <Badge variant="neutral">À SURVEILLER</Badge>
                      )}
                    </div>
                  </td>
                  <td className="rd-cell-token">
                    <span className="rd-token-sym">{w.discovery_token_symbol || '—'}</span>
                    {w.entry_delay_min != null && (
                      <span className="rd-token-delay">+{w.entry_delay_min}min</span>
                    )}
                  </td>
                  <td className={`rd-cell-num ${(w.gain_pct ?? 0) >= 0 ? 'rd-pos' : 'rd-neg'}`}>
                    {fmtPct(w.gain_pct)}
                  </td>
                  <td className="rd-cell-num">{fmtUsd(w.cumul_amount_usd)}</td>
                  <td>
                    <span className="rd-score-disc">{w.discovery_score ?? '—'}</span>
                  </td>
                  <td>
                    {w.follow_score != null ? (
                      <span className="rd-follow-wrap">
                        <span className={`rd-follow-score rd-follow-score--${
                            w.follow_recommendation === 'Suivre'        ? 'green'
                          : w.follow_recommendation === 'Ne pas suivre' ? 'red'
                          : 'orange'
                        }`}>
                          {w.follow_score}
                        </span>
                        <span className="rd-follow-rec">{w.follow_recommendation}</span>
                      </span>
                    ) : (
                      <span className="rd-not-scored">Pas encore scoré</span>
                    )}
                  </td>
                  <td className="rd-cell-del">
                    {onCopyWallet && (
                      <button
                        className="rd-del-btn"
                        onClick={() => onCopyWallet(w)}
                        title="Créer une config Copytrade pour ce wallet"
                        style={{ background: 'rgba(0,229,204,0.08)', borderColor: '#00e5cc22', color: '#00e5cc' }}
                      >+</button>
                    )}
                    <button
                      className="rd-del-btn"
                      onClick={() => handleDeleteWallet(w)}
                      disabled={isImpersonating}
                      title="Supprimer ce wallet du Radar"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
